/**
 * SMS 서비스
 * FCM을 통한 SMS 발송 API 호출 담당
 */

import { useApi } from '@/composables/useApi'
import type {
  SendSmsRequest,
  SendSmsResponse,
  SmsMessage,
} from '@farmfree/common/types/message/fcmMessage'

/**
 * SMS 발송 API 호출
 * @param uid 사용자 UID
 * @param messages 발송할 메시지 목록
 * @returns SMS 발송 응답
 */
export async function sendSmsBatch(uid: string, messages: SmsMessage[]): Promise<SendSmsResponse> {
  try {
    const api = useApi()

    const request: SendSmsRequest = {
      uid,
      messages,
    }

    const { data } = await api.post<SendSmsResponse>('/api/sms/send-batch', request)

    return data
  } catch (error: any) {
    console.error('❌ SMS 발송 API 호출 실패:', error)

    // axios 에러 응답 처리
    if (error.response?.data) {
      return error.response.data as SendSmsResponse
    }

    // 네트워크 에러 등 기타 에러
    return {
      success: false,
      message: '네트워크 오류가 발생했습니다',
      error: error.message || 'Unknown error',
    }
  }
}

/**
 * 단일 SMS 발송 헬퍼 함수
 * @param uid 사용자 UID
 * @param phoneNumber 수신자 전화번호
 * @param message 메시지 내용
 * @param messageId 메시지 ID (옵션)
 * @returns SMS 발송 응답
 */
export async function sendSingleSms(
  uid: string,
  phoneNumber: string,
  message: string,
  messageId?: string
): Promise<SendSmsResponse> {
  const smsMessage: SmsMessage = {
    phoneNumber,
    message,
    messageId,
  }

  return sendSmsBatch(uid, [smsMessage])
}

/**
 * 전화번호 정규화 (통합 유틸리티 사용)
 */
export { normalizePhoneNumber } from '@/utils/phone'

/**
 * 메시지 ID 생성
 * @returns 고유한 메시지 ID
 */
export function generateMessageId(): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substr(2, 9)
  return `msg_${timestamp}_${random}`
}

/**
 * SMS 동기화 요청
 * Admin → Functions → FCM(SMS_SYNC) → 앱 → 메시지 동기화
 * @param uid 사용자 UID
 * @returns 동기화 요청 응답
 */
export async function requestSmsSync(uid: string): Promise<{ success: boolean; message: string }> {
  try {
    const api = useApi()

    const { data } = await api.post<{ success: boolean; message: string }>(
      '/api/sms/request-sync',
      { uid }
    )

    return data
  } catch (error: any) {
    console.error('❌ SMS 동기화 요청 실패:', error)

    if (error.response?.data) {
      return error.response.data
    }

    return {
      success: false,
      message: '네트워크 오류가 발생했습니다',
    }
  }
}
